import axios from 'axios';
import buildPaginationQueryOpts from '@/shared/sort/sorts';
import {BASE_API_URL} from '@/constants';
import {PREFIX_API_PUBLIC} from '@/constants';
import {IInscription} from '@/shared/model/inscription.model';

const entityName = 'inscriptions';

export default class InscriptionService {
  /**
   * Retrieve a registration by its ID
   * @param id  registration ID
   */
  public find(id: number): Promise<IInscription> {
    return new Promise<IInscription>((resolve, reject) => {
      axios
        .get(`${BASE_API_URL}${entityName}/${id}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  /**
   * Check if a previous payed registration exists with same firstName, lastName, email and examType
   * @param inscription
   */
  public checkPreviousRegistration(inscription: IInscription): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      axios
        .post(`${BASE_API_URL}${PREFIX_API_PUBLIC}${entityName}/check-previous-registration`, inscription)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  /**
   * Retrieve all registrations for one examSession
   * @param id   selected examSession ID
   */
  public findBySession(id: number): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .get(`${BASE_API_URL}${entityName}/session/${id}`)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  /**
   * Retrieve all registrations for one examSession with pagination
   * @param examSessionId   selected examSession ID
   * @param paginationQuery
   */
  public retrieveBySession(examSessionId: number, paginationQuery?: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .get(BASE_API_URL + entityName + `BySession/${examSessionId}?${buildPaginationQueryOpts(paginationQuery)}`)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  /**
   * Retrieve all registrations
   * @param paginationQuery
   */
  public retrieve(paginationQuery?: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .get(BASE_API_URL + entityName + `?${buildPaginationQueryOpts(paginationQuery)}`)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  /**
   * Retrieve all registrations by criteria
   * @param criteria  filter criteria
   * @param paginationQuery
   */
  public retrieveFiltered(criteria: IInscription, paginationQuery?: any): Promise<any> {
    let criteriaParams = `?${buildPaginationQueryOpts(paginationQuery)}`;
    if (criteria.hasPaid !== undefined) {
      criteriaParams += `&hasPaid.equals=${criteria.hasPaid}`;
    }
    if (criteria.numDossier !== undefined && criteria.numDossier !== null && criteria.numDossier !== '') {
      criteriaParams += `&numDossier.in=${criteria.numDossier}`;
    }
    if (criteria.lastName !== undefined && criteria.lastName !== null && criteria.lastName !== '') {
      criteriaParams += `&lastName.contains=${criteria.lastName}`;
    }
    if (criteria.firstName !== undefined && criteria.firstName !== null && criteria.firstName !== '') {
      criteriaParams += `&firstName.contains=${criteria.firstName}`;
    }
    if (criteria.birthDate !== undefined && criteria.birthDate !== null) {
      criteriaParams += `&birthDate.equals=${criteria.birthDate.toISOString()}`;
    }
    return new Promise<any>((resolve, reject) => {
      axios
        .get(`${BASE_API_URL}${entityName}${criteriaParams}`)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }


  /**
   * Download registration form PDF for inscription by ID
   */
  public downloadRegistrationForm(inscriptionId) {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(`${BASE_API_URL}${entityName}/download-registration-form`, inscriptionId, {headers: {'Content-Type': 'application/json'}, responseType: 'blob'})
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  /**
   * Export all registrations as CSV file for A-REGIE
   */
  public exportInscriptionsAsCsv() {
    return new Promise<any>((resolve, reject) => {
      axios
        .post(`${BASE_API_URL}${entityName}/export-all`, {responseType: 'blob'})
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  /**
   * Delete registration by its ID
   * @param id
   */
  public delete(id: number): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .delete(`${BASE_API_URL}${entityName}/${id}`)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  /**
   * Create a new registration.
   * Call to a private API (authentication needed).
   * Used mostly for back-office.
   * @param entity  registration to create
   */
  public create(entity: IInscription): Promise<IInscription> {
    return new Promise<IInscription>((resolve, reject) => {
      axios
        .post(`${BASE_API_URL}${entityName}`, {
          inscription: entity,
          paymentType: entity.paymentType,
        })
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  /**
   * Create a new registration.
   * Call to a public API (no need to be authenticated).
   * Used, for instance, by inscription form.
   * @param entity  registration to create
   */
  public createPublic(entity: IInscription): Promise<IInscription> {
    return new Promise<IInscription>((resolve, reject) => {
      axios
        .post(`${BASE_API_URL}${PREFIX_API_PUBLIC}${entityName}`, entity)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  /**
   * Update a registration
   * @param entity  registration to update
   */
  public update(entity: IInscription): Promise<IInscription> {
    return new Promise<IInscription>((resolve, reject) => {
      axios
        .put(`${BASE_API_URL}${entityName}`, entity)
        .then(res => {
          resolve(res.data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  /**
   * Get registration fee amount
   */
  public getRegistrationFee() {
    return new Promise<any>((resolve, reject) => {
      axios
        .get(BASE_API_URL + PREFIX_API_PUBLIC + entityName + '/registration-fee')
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }
}
